import React, { useState, useEffect, FunctionComponent } from "react";
import { Car as CarProp, fetchCar } from "../../services/mobile";
import SimpleReactLightbox, { SRLWrapper } from "simple-react-lightbox";
import { useQuery } from "../../hooks/useQuery";
import Header from "../../styles/Header";
import styled from "styled-components";
import theme from "../../styles/theme";
import H1 from "../../styles/H1";
import CarFeatures from "../../components/CarFeatures";
import H2 from "../../styles/H2";
import Button from "../../styles/Button";
import Price from "../../styles/Price";
import Paragraph from "../../styles/Paragraph";
import BoxHighlight from "../../styles/BoxHighlight";
import ContactImage from "../../assets/img/marco-150x150.png";
import PageWrapper from "../../components/PageWrapper";
import Box from "../../styles/Box";
import { ReactComponent as LoadingIcon } from "../../assets/svg/loading.svg";
import { Settings } from "../../context/SettingsContext/SettingsContext";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${theme.mediaQueries.tablet}px) {
    display: block;
  }
`;

const CarWrapper = styled.div`
  @media (min-width: ${theme.mediaQueries.tablet}px) {
    width: 680px;
    margin-right: ${theme.unit * 2}px;
    float: left;
  }
`;

const Sidebar = styled.div`
  @media (min-width: ${theme.mediaQueries.tablet}px) {
    width: 400px;
    float: right;
  }
`;

const ContentWrapper = styled.div`
  margin-bottom: ${theme.unit * 2}px;
`;

const MainImageWrapper = styled.div`
  overflow: hidden;
  border-radius: 0px 0px 20px 0px;
  margin-bottom: ${theme.unit * 2}px;
  position: relative;
`;

const MainImage = styled.img`
  max-width: 100%;
  display: block;
`;
const MainImageButton = styled(Button)`
  position: absolute;
  right: 0;
  bottom: 0;
  border-radius: 0;
  pointer-events: none;
`;

const Thumb = styled.img`
  display: none;
`;

const PriceWrapper = styled.div`
  background: ${theme.colors.white};
  padding: ${theme.unit}px ${theme.unit * 2}px;
  box-shadow: 0 0.4px 3.7px rgba(0, 0, 0, 0.07),
    0 3.2px 7.5px rgba(0, 0, 0, 0.06), 0 9.3px 13px rgba(0, 0, 0, 0.051),
    0 20.6px 24.9px rgba(0, 0, 0, 0.043), 0 41.3px 59.2px rgba(0, 0, 0, 0.037),
    0 93px 161px rgba(0, 0, 0, 0.03);
`;

const ListWrapper = styled.div`
  ul li {
    margin-top: -3px;
    height: 28px;
    list-style-image: url("https://mondialautomotive.nl/wp-content/themes/convident-theme-child/img/check.svg");
    list-style-position: outside;
  }
`;

const Specifications = styled.div`
  > div {
    display: flex;

    > div {
      width: 50%;
      padding: 6px 0px;
      font-size: 1.15rem;

      &:first-child {
        font-weight: bold;
      }
    }
  }
`;

const Features = styled.div`
  > div {
    border-bottom: 0.5px solid ${theme.colors.blue};
    margin-bottom: ${theme.unit * 0.5}px;
    padding-bottom: ${theme.unit * 0.5}px;
    line-height: 1;
    font-size: 1.15rem;
  }
`;

const ContactWrapper = styled.div`
  display: flex;
  align-items: flex-start;

  img {
    width: 56px;
    height: 56px;
    margin-bottom: ${theme.unit}px;
    margin-right: ${theme.unit}px;
    border-radius: 28px;
  }
`;

const specificationsMap: Array<{
  label: string;
  key: string;
  suffix?: string;
  whenTrue?: string;
  whenFalse?: string;
}> = [
  {
    label: "Brandstof",
    key: "fuel",
  },
  {
    label: "Transmissie",
    key: "gearbox",
  },
  {
    label: "Bouwjaar",
    key: "construction-year",
  },
  {
    label: "Eerste toelating",
    key: "first-registration",
  },
  {
    label: "Exterieurkleur",
    key: "exterior-color",
  },
  {
    label: "Interieurkleur",
    key: "interior-color",
  },
  {
    label: "Bekleding",
    key: "interior-type",
  },
  {
    label: "Aantal deuren",
    key: "door-count",
  },
  {
    label: "Aantal stoelen",
    key: "num-seats",
  },
  {
    label: "Uitstoot CO-2",
    key: "emission-fuel-consumption",
    suffix: " g/km",
  },
  {
    label: "Inhoud",
    key: "cubic-capacity",
    suffix: " cm3",
  },
  {
    label: "Emissieklasse",
    key: "emission-class",
  },
  {
    label: "Verbruik binnen de bebouwde kom",
    key: "emission-fuel-consumption-inner",
    suffix: " liter / 100km",
  },
  {
    label: "Verbruik buiten de bebouwde kom",
    key: "emission-fuel-consumption-outer",
    suffix: " liter / 100km",
  },
  {
    label: "Verbruik gecombineerd",
    key: "emission-fuel-consumption-combined",
    suffix: " liter / 100km",
  },
];

interface Props {
  settings: Settings;
}

const Car: FunctionComponent<Props> = ({ settings }) => {
  const params = useQuery();
  const carId = params.get("id");
  const [car, setCar] = useState<CarProp | null>(null);
  useEffect(() => {
    const initialize = async () => {
      if (carId) {
        const response = await fetchCar(parseInt(carId), settings);
        if (response) {
          setCar(response.car);
        }
      }
    };
    initialize();
  }, [carId, settings]);

  useEffect(() => {
    window.scrollTo(0, 0);
    //@ts-ignore
    if (window.ga) {
      //@ts-ignore
      window.ga("send", "pageview", window.location.pathname);
    }
  }, []);

  if (!car) {
    return (
      <Box centered>
        <LoadingIcon />
      </Box>
    );
  }

  const name = `${car.brand} ${car.modelDescription}`;
  return (
    <PageWrapper>
      <Wrapper>
        <Header />
        <CarWrapper>
          <ContentWrapper>
            <H1>{name}</H1>
            <CarFeatures car={car} />
          </ContentWrapper>
          <SimpleReactLightbox>
            <SRLWrapper
              options={{
                settings: {
                  autoplaySpeed: 0,
                  hideControlsAfter: false,

                },
                buttons: {
                  showDownloadButton: false,
                },
              }}
            >
              {car.images.length > 0 && (
                <MainImageWrapper>
                  <MainImage alt={name} src={car.images[0]} />
                  {car.images.length > 1 && (
                    <MainImageButton>
                      Bekijk alle {car.images.length + 1} foto's in de
                      fotogalerij
                    </MainImageButton>
                  )}
                </MainImageWrapper>
              )}
              {car.images
                .filter((_, index) => index !== 0)
                .map((imageSrc) => (
                  <Thumb alt={name} key={imageSrc} height="50" src={imageSrc} />
                ))}
            </SRLWrapper>
          </SimpleReactLightbox>
        </CarWrapper>
        <Sidebar>
          <ContentWrapper>
            <PriceWrapper>
              <div>
                <Paragraph>Dit is onze all-in prijs voor deze auto:</Paragraph>
                <Price car={car} />
                <Paragraph>Inclusief:</Paragraph>
                <ListWrapper>
                  <ul>
                    <li>Aanschafwaarde</li>
                    <li>Rest-BPM</li>
                    <li>Importkosten</li>
                    <li>6 maanden garantie</li>
                  </ul>
                </ListWrapper>
              </div>
              <div></div>
            </PriceWrapper>
          </ContentWrapper>
          <ContentWrapper>
            <BoxHighlight color="alternate" inline>
              <H2>Vrijblijvend contact opnemen over deze auto</H2>
              <ContactWrapper>
                <img src={ContactImage} alt="Marco" />
                <Paragraph>
                  Alle mogelijkheden op een rij, of juist een specifieke offerte
                  ontvangen? Bij onze specialist Marko ben je aan het juiste
                  adres.
                </Paragraph>
              </ContactWrapper>
              <Button
                color="alternate"
                large
                onClick={() => {
                  window.location.assign(
                    `/keuzehulp-via-url/?id=${car.id}&brand=${car.brand}&model=${car.model}&price=${car.price}`
                  );
                }}
              >
                Neem contact op
              </Button>
            </BoxHighlight>
          </ContentWrapper>
        </Sidebar>
        <CarWrapper>
          <ContentWrapper>
            <H2 icon>De belangrijkste specificaties</H2>
            <Specifications>
              {specificationsMap
                .filter(
                  (specification) =>
                    specification.whenTrue ||
                    specification.whenFalse ||
                    (specification.key in car.specifics &&
                      car.specifics[specification.key])
                )
                .map((specification) => (
                  <div key={specification.key}>
                    <div>{specification.label}</div>
                    <div>
                      {specification.whenTrue &&
                        !!car.specifics[specification.key] &&
                        specification.whenTrue}
                      {specification.whenFalse &&
                        !car.specifics[specification.key] &&
                        specification.whenFalse}
                      {!specification.whenTrue &&
                        !specification.whenFalse &&
                        car.specifics[specification.key]}
                      {specification.suffix && specification.suffix}
                    </div>
                  </div>
                ))}
              <div>
                <div>BTW verrekenbaar</div>
                <div>{car.vatable ? "Ja" : "Nee"}</div>
              </div>
            </Specifications>
          </ContentWrapper>
          <ContentWrapper>
            <H2 icon>Alle opties</H2>
            <Features>
              {car.features.map((feature) => (
                <div key={feature.value}>{feature.value}</div>
              ))}
            </Features>
          </ContentWrapper>
        </CarWrapper>
      </Wrapper>
    </PageWrapper>
  );
};

export default Car;
